<template>
  <el-card>
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">{{ $t('common.devices') }}</span>
      <span class="ring-card__header-buttons">
        <RefreshButton @click.native="getDeviceRegistries()" />
      </span>
    </div>
    <div class="card-content device-list">
      <el-alert
          v-if="errorMessage"
          title="Customer Devices"
          type="warning"
          :closable="false"
          :description="errorMessage"
          show-icon />

      <div
          v-else
          v-loading="!customerId || loading"
          style="min-height: 100px; margin: 0px 0px 15px">
        <span style="font-size: 18px;display: inline-flex; margin: 0px 10px 15px"> Location: </span>
        <el-select
            v-model="selectedLocation"
            placeholder="Customer Locations"
            style="max-width: 320px"
            @change="getDeviceRegistries()">
          <template #prefix>
            <i
                v-if="isResidentialLocation(selectedLocation)"
                class="el-icon-s-home"></i>
            <i
                v-else
                class="el-icon-office-building"></i>
          </template>
          <el-option
              v-for="(item, index) in accountLocations"
              :key="`${item.value}-${index}`"
              :label="item.label"
              :value="item">
            <template #default>
              <i
                  v-if="isResidentialLocation(item)"
                  class="el-icon-s-home"></i>
              <i
                  v-else
                  class="el-icon-office-building"></i>
              <span>{{ item.label }}</span>
            </template>
          </el-option>
        </el-select>
        <span
            v-if="rbacEnabled"
            id="role-description"
            class="ring-card-action-box__text ring-card-action-box__description"
            style="margin: 0px 10px 15px">{{ getCustomerLocationRoleDescription() }}</span>

        <el-table
            v-if="tableReady"
            v-loading="loading"
            class="table__header-subtitles"
            :row-class-name="'device-table-row-style'"
            :cell-class-name="'device-table-cell-style'"
            :data="filteredDevices"
            :highlight-current-row="false">
          <el-table-column
              min-width="90"
              label="Device">
            <template slot-scope="scope">
              <div style="text-align: center;">
                <el-popover
                    placement="top-start"
                    width="200"
                    trigger="hover">
                  <span
                      v-if="scope.row.deleted_at !== undefined"
                      class="offline-device__icon">Deleted on {{ scope.row.deleted_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span
                      v-else-if="scope.row.deactivated_at !== undefined"
                      class="offline-device__icon">Deactivated on {{ scope.row.deactivated_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span
                      v-else
                      class="online-device__icon">Activated on {{ scope.row.created_at | moment("MMMM Do YYYY, h:mm a") }}</span>
                  <span slot="reference">
                    <span
                        v-if="scope.row.deleted_at !== undefined"
                        class="offline-device__icon">Deleted</span>
                    <span
                        v-else-if="scope.row.deactivated_at !== undefined"
                        class="offline-device__icon">Deactivated</span>
                    <span
                        v-else
                        class="online-device__icon">Active</span>
                  </span>
                </el-popover>


                <DeviceImage
                    :device="scope.row" />
                <i
                    v-if="statuses.loading"
                    class="el-icon-loading"></i>
                <span v-else-if="isFirstClassDevice(scope.row)">
                  <span
                      v-if="get(statuses.devices[scope.row.doorbot_id], 'connected', false)"
                      class="online-device__icon">Online</span>
                  <span v-else-if="statuses.devices[scope.row.doorbot_id] === undefined"></span>
                  <span
                      v-else-if="get(statuses.devices[scope.row.doorbot_id], 'service') === 'DHS' && get(statuses.devices[scope.row.doorbot_id], 'connected') === null"
                      class="offline-device__icon">N/A</span>
                  <span
                      v-else
                      class="offline-device__icon">Offline</span>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="500">
            <template slot-scope="scope">
              <div style="padding-left: 10px; text-align: justify">
                <div style="border-bottom: solid 1px #ddd; margin-bottom: 10px; padding-bottom: 10px;">
                  {{ scope.row.name }}
                  <br>
                  <span style="color: #999">{{ (product(scope.row).description || scope.row.type) | capitalize }}</span>
                </div>
                <div class="device-column">
                  <div style="position: relative">
                    <el-link
                        type="primary"
                        target="_self"
                        :underline="false"
                        :href="deviceRoute(scope.row)">
                      {{ deviceLinkText(scope.row) }}
                    </el-link>
                  </div>

                  <div v-if="hasRingSolutionsUnavailable(scope.row)">
                    <span class="ring-solution-unavailable">{{ getRingSolutionsUrlUnavailableDescription(scope.row.ring_solutions_url_unavailable) }}</span>
                  </div>
                  <div v-if="hasRingSolutionsUrl(scope.row)">
                    <AlarmDesktopLink :url="scope.row.ring_solutions_url" />
                  </div>
                  <br>
                  <div>
                    <i
                        v-if="warranty.loading"
                        class="el-icon-loading"></i>
                    <div
                        v-else
                        style="align-items: center; margin-left: -1em"
                        class="warranty-status flex flex-row">
                      <div
                          v-if="delayedFulfillmentEnabled"
                          class="flex-none"
                          style="max-width: fit-content;">
                        <el-alert
                            class="coverage-alerts"
                            title="Fulfillment"
                            :closable="false"
                            type="info">
                          <StatusTag
                              :is-error="getFulfillmentStatus(eligibility[scope.row.device_id].isDelayedFulfillment) !== 'Standard'"
                              :disable-tip="!showFulfillmentReason(eligibility[scope.row.device_id].isDelayedFulfillment)"
                              :status-message="getFulfillmentStatus(eligibility[scope.row.device_id].isDelayedFulfillment)"
                              style="font-size: 13px;">
                            <template v-slot:popover-content>
                              <FulfillmentReasons :reason="(eligibility[scope.row.device_id] && eligibility[scope.row.device_id].delayedFulfillmentReason) || ''" />
                            </template>
                          </StatusTag>
                        </el-alert>
                      </div>
                      <div
                          v-if="delayedFulfillmentEnabled"
                          class="custom-divider"></div>
                      <div
                          class="flex-none"
                          style="max-width: fit-content; margin-left: 5px">
                        <el-alert
                            class="coverage-alerts"
                            title="Warranty"
                            :closable="false"
                            type="info">
                          <div
                              class="flex-col">
                            <WithVariable v-slot:default="{ w = deviceWarranty(scope.row) }">
                              <StatusTag
                                  style="font-size: 13px;"
                                  :status-message="w.status"
                                  :is-error="!w.in_warranty">
                                <template v-slot:popover-content>
                                  <WarrantyInfo :data="w" />
                                </template>
                              </StatusTag>
                            </WithVariable>
                          </div>
                        </el-alert>
                      </div>
                      <div class="custom-divider"></div>
                      <div
                          class="flex-none"
                          style="max-width: fit-content; margin-left: 5px">
                        <el-alert
                            class="coverage-alerts"
                            title="Eligibility"
                            :closable="false"
                            type="info">
                          <EligibilityTags
                              v-if="eligibility[scope.row.device_id]"
                              style="font-size: 13px;"
                              :eligibility="eligibility[scope.row.device_id]" />
                        </el-alert>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="140"
              label="Location">
            <template slot-scope="scope">
              <div>{{ get(scope.row, 'location.name', '') | truncate(20) }}</div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="220"
              label="Users">
            <template slot-scope="scope">
              <div
                  slot="content"
                  class="flex flex-col items-start">
                <div
                    v-for="user in scope.row.authorized_users.owners"
                    :key="user.id">
                  <b> {{ user.email | truncate(30) }}</b>
                </div>
                <div
                    v-for="user in scope.row.authorized_users.users"
                    :key="user.id">
                  <span> {{ user.email | truncate(30) }} </span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="160"
              label="Health">
            <template
                v-if="isFirstClassDevice(scope.row)"
                slot-scope="scope">
              <div
                  slot="content">
                <div class="flex items-start">
                  <DiagnosticHealth
                      :popover="true"
                      :report-time-stamp="get(scope.row, 'last_status_report.timestamp', '')"
                      :diagnostic="get(scope.row, 'health_information.status_report', [])" />
                </div>
                <br>
                <div
                    v-if="supportsBitrateInfo(scope.row)"
                    class="flex flex-col">
                  <BitrateInfoPopover
                      class="flex-grow"
                      style="padding-bottom: 7px;"
                      :device="scope.row">
                    Bitrate
                  </BitrateInfoPopover>
                  <div class="flex flex-row">
                    <el-tooltip
                        class="flex-grow"
                        content="Device offline or unavailable"
                        :disabled="isDeviceOffline(scope.row) === false"
                        placement="bottom">
                      <ThroughputTest
                          placement="left"
                          :device-kind="scope.row.type"
                          @execute="(test_type, done) => executeTest(scope.row, test_type, done)">
                        <template v-slot:default>
                          <el-button
                              :disabled="isDeviceOffline(scope.row)"
                              style="min-width: 100%"
                              plain
                              size="mini"
                              type="info">
                            Iperf Test
                          </el-button>
                        </template>
                      </ThroughputTest>
                    </el-tooltip>
                    <SummaryReportPopover
                        title="Iperf Test Result"
                        :iperf-only="true"
                        :report="tTest.devices[scope.row.doorbot_id].result">
                      <template v-slot:default>
                        <el-tag
                            style="margin-left: 3px"
                            size="medium"
                            type="info">
                          <font-awesome-icon
                              style="display: inline-block; padding: 0 5px 0 5px"
                              :icon="faInfo" />
                        </el-tag>
                      </template>
                    </SummaryReportPopover>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="100"
              label="Power">
            <template
                v-if="isFirstClassDevice(scope.row)"
                slot-scope="scope">
              <div
                  slot="content"
                  class="flex items-start">
                <span>{{ scope.row.power_source }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              min-width="100"
              label="Network">
            <template
                v-if="isFirstClassDevice(scope.row)"
                slot-scope="scope">
              <div
                  slot="content"
                  class="flex items-start">
                <b>{{ networkName(scope.row) }}</b>
              </div>
            </template>
          </el-table-column>
          <el-table-column min-width="120">
            <template slot="header">
              Tag&nbsp;
              (<a
                  href="https://ring.com/support/internal/articles/per08/tags-in-neighbor-console-and-neighbor-dashboard"
                  target="_blank"
                  style="text-decoration: none; color: #1998d5;">
                Details
              </a>)
            </template>
            <template slot-scope="scope">
              <i
                  v-if="troubleshooting.loading"
                  class="el-icon-loading"></i>
              <Tags
                  v-else
                  slot="content"
                  tags-direction="col"
                  :list="dynamicTags(scope.row)" />
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
            v-show="pagination.show"
            background
            style="padding: 25px 0 20px"
            :hide-on-single-page="true"
            :current-page.sync="pagination.currentPage"
            :page-size="pagination.size"
            layout="prev, pager, next"
            :total="pagination.total"
            @current-change="handlePageChange" />
      </div>
    </div>
  </el-card>
</template>

<script>
import Vue from "vue";
import { faInfo, faExternalLinkAlt } from "@fortawesome/fontawesome-free-solid";
import DiagnosticHealth from "@/components/Shared/Device/DiagnosticHealth";
import SummaryReportPopover from "@/containers/Customer/Devices/StatusReports/SummaryReportPopover";
import { get, startCase, forEach, keys, capitalize } from "lodash";
import Tags from "@/components/Shared/Tags";
import DeviceImage from "@/components/Shared/DeviceImage";
import RefreshButton from "@/containers/Customer/RefreshButton";
import {
  DeviceWarrantyAndEligibilityPolicy,
  Warranty,
} from "@/helpers/DeviceWarrantyAndEligibilityPolicy";
import { getRBACRoleDescription } from "@/utils/utils";

/* ---------------------
 * Import from the Store
 * --------------------- */
import { createNamespacedHelpers } from "vuex";
const {
  mapGetters: deviceTableGetters,
  mapActions: deviceTableActions,
  mapMutations: deviceTableMutations,
} = createNamespacedHelpers("db/devices");
const { mapActions: deviceActions } = createNamespacedHelpers("db/device");
const { mapGetters: accountGetters } = createNamespacedHelpers("db/account");
const { mapGetters: sharedGetters } = createNamespacedHelpers("shared");
const { mapActions: uiActions, mapGetters: uiGetters } =
  createNamespacedHelpers("ui");

const IPERF_TEST_CONTEXT = 13;

import ThroughputTest from "@/components/Shared/Device/Settings/ThroughputTest";
import BitrateInfoPopover from "@/components/Shared/Device/BitrateInfoPopover";
import AlarmDesktopLink from "./Shared/AlarmDesktopLink";
import WarrantyInfo from "@/components/Shared/WarrantyInfo";
import deviceUtils from "@/mixins/deviceUtils";
import FulfillmentReasons from "@/components/Shared/FulfillmentReasons.vue";
import StatusTag from "@/components/Shared/StatusTag.vue";

import permission from "@/mixins/permission";
import EligibilityTags from "@/components/Shared/EligibilityTags.vue";

const { mapGetters } = createNamespacedHelpers("db/account");

/* ---------------------
 *    Vue Component
 * --------------------- */

export default {
  name: "Devices",
  components: {
    EligibilityTags,
    WarrantyInfo,
    AlarmDesktopLink,
    Tags,
    DiagnosticHealth,
    ThroughputTest,
    BitrateInfoPopover,
    SummaryReportPopover,
    DeviceImage,
    RefreshButton,
    FulfillmentReasons,
    StatusTag,
  },
  mixins: [deviceUtils, permission],
  props: {
    customerId: {
      type: [String, Number],
      default() {
        return "";
      },
    },
  },
  data() {
    return {
      show: "show",
      canUpdateEmail: { permission: "update-email", on: "Customer" },
      tTest: {
        devices: {},
      },
      faInfo,
      faExternalLinkAlt,
      baseUrl: process.env.API_URL,
      errorMessage: false,
      shouldFetchDevices: true,
      loading: false,
      pagination: { currentPage: 1, size: 20, total: 0, show: false },
      statuses: { loading: false, devices: {} },
      warranty: { loading: true, devices: {} },
      troubleshooting: { loading: true, devices: {} },
      vendorNames: { loading: false, devices: {} },
      env: {},
      loadingBitRate: false,
      loadingIperfTest: false,
      enableIperfTest: false,
      currentDevice: {},
      currentEvent: { ding_id: "..." },
      bitrateData: {},
      iperfOptions: [
        { label: "iperf_tcp", int: 1, checked: false },
        { label: "iperf_udp_1M", int: 2, checked: false },
        { label: "iperf_udp_2M", int: 4, checked: false },
        { label: "iperf_udp_4M", int: 8, checked: false },
        { label: "ping", int: 16, checked: false },
        { label: "channel_score", int: 32, checked: false },
      ],
      warrantyData: {},
      eligibility: {},
      selectedLocation: {
        label: null,
        value: null,
        smb_role: "owner",
      },
      accountLocations: [],
    };
  },
  beforeRouteEnter(to, from, next) {
    if (to.path.match(/^\/customer\/(ring.entity.([^\/]+))\/devices\/?$/)) {
    } else {
      next();
    }
  },
  computed: {
    ...sharedGetters([
      "userHasPermission",
      "products",
      "delayedFulfillmentEnabled",
      "delayedFulfillmentTipsEnabled",
    ]),
    ...deviceTableGetters(["devices"]),
    ...accountGetters({ email: "email", storedCustomerId: "customerId" }, [
      "user",
    ]),
    ...uiGetters(["isLoading"]),
    ...mapGetters(["rbacEnabled"]),
    OOWFlow() {
      return this.hasPermission("NewUI", "OOWFlow");
    },
    filteredDevices() {
      let devices = this.devices;

      if (this.selectedLocation.value) {
        devices = devices.filter((device) => {
          // Only shows devices for the selected location
          const sameLocation =
            get(device.location, "location_id") === this.selectedLocation.value;
          return sameLocation;
        });
      }

      // Verify if user has access to these devices
      devices = devices.filter((device) => {
        // Owner always has access to the device
        if (device.owner_id == this.customerId) return true;

        // Shared users have access to the device
        const authorizedUserIds = device.authorized_users.users.map(
          (u) => u.customer_id
        );
        if (authorizedUserIds.includes(this.customerId)) return true;

        // SMB users could have access to the device
        const smbAccess =
          device.smb_user_id == this.customerId &&
          get(device, "smb_roles", []).length > 0;
        if (smbAccess) return true;

        return false;
      });

      // Sort devices by name
      return devices.sort((a, b) => a.name.localeCompare(b.name));
    },
    deviceTable() {
      return this.devices;
    },
    tableReady() {
      return get(this, "devices", []) && this.loading === false;
    },
  },
  watch: {
    customerId(customerIdProp) {
      // if there is a different customer or the device list is empty
      if (
        String(this.storedCustomerId) !== String(customerIdProp) ||
        this.devices.length === 0
      ) {
        this.resetState();
        this.getDeviceRegistries();
      }
    },
    devices(value) {
      const list = value || [];
      if (list.length > 0) {
        this.tTest.devices = list.reduce((devices, current) => {
          devices[current.doorbot_id] = {
            visible: false,
            doorbot_id: current.doorbot_id,
            working: false,
            result: get(current, "last_status_report", null),
          };
          return devices;
        }, {});
        this.$nextTick(() => {
          this.fetchStatuses();
          this.fetchWarranties();
          this.fetchTroubleshooting();
        });
      }
    },
  },
  created() {
    this.env = process.env;
    if (this.customerId) this.getDeviceRegistries();
  },
  methods: {
    showFulfillmentReason(status) {
      return this.delayedFulfillmentTipsEnabled && status;
    },
    getCustomerLocations() {
      //Only on the first load
      if (this.selectedLocation.value == null) {
        let locations = this.$store.getters["db/account/locations"];
        locations.forEach((location) => {
          this.accountLocations.push({
            label: location.name,
            value: location.location_id,
            smb_role: location.smb_role || "owner",
          });
        });
        this.accountLocations.sort((a, b) => {
          // First, sort by whether smb_role exists (non-owner roles first)
          if (a.smb_role !== "owner" && b.smb_role === "owner") return -1;
          if (a.smb_role === "owner" && b.smb_role !== "owner") return 1;
          // Then sort alphabetically by label
          return a.label.localeCompare(b.label);
        });
        this.selectedLocation = this.accountLocations[0];
      }
    },
    getCustomerLocationRoleDescription() {
      if (this.isResidentialLocation(this.selectedLocation))
        return "This is a Residential Location.";

      const smbRole = get(this.selectedLocation, "smb_role");
      const role = getRBACRoleDescription(smbRole);
      if (!!role) return `This is a Business Location. User role is ${role}`;

      return "";
    },
    formatManufacturedAt(manufacturedAt) {
      manufacturedAt = this.$moment(manufacturedAt);
      if (!manufacturedAt.isValid()) return "";
      return this.$moment(manufacturedAt).utc().format("MM/DD/YYYY");
    },
    deviceWarranty(device) {
      let id = device.mac_address || device.hardware_id;
      return this.warranty.devices[id]?.warranty || { info: {} };
    },
    deviceLinkText(data) {
      return (
        get(data, "mac_address", null) ||
        get(data, "hardware_id", null) ||
        get(data, "doorbot_id", "ID not found")
      );
    },
    deviceRoute(row) {
      let id = get(row, "owner_id", "no-owner-found");
      return `/customer/${id}/devices/${row.doorbot_id}`;
    },
    ...deviceTableActions([
      "fetchDeviceList",
      "getDeviceStatuses",
      "fetchDeviceRegistries",
      "getWarrantyAndEligibilityStatus",
      "getDeviceTroubleshooting",
    ]),
    ...deviceTableMutations(["resetState"]),
    ...deviceActions([
      "loadLastStatusReport",
      "getLastBitrate",
      "execThroughputTest",
    ]),
    ...uiActions(["setLoading"]),
    get: get,
    startCase: startCase,
    networkName(device) {
      // return get(device, "last_status_report.data.status.bssid", false);
      return get(device, "last_setup.metadata.network_config_ssid", "unknown");
    },
    dynamicTags(d) {
      let devices = this.troubleshooting.devices;
      return (
        devices[d.mac_address] ||
        devices[d.doorbot_id] ||
        devices[d.hardware_id] ||
        []
      );
    },
    isFirstClassDevice(device) {
      return (
        /(chime)|(base_station)|(beams)|(vehicle_brody_v1)|(tracker_buddy)|(sensor_bluejay_)/.test(
          device.type
        ) === false
      );
    },
    isDeviceOffline(device) {
      return (
        get(this.statuses.devices, [device.doorbot_id, "connected"], false) !==
        true
      );
    },
    isBaseStation(device) {
      return /base_station/.test(device.type);
    },
    isJaws(device) {
      return !!Boolean(device.type.match("jaws"));
    },
    isBlueJay(device) {
      return /sensor_bluejay_/.test(device.type);
    },
    isChime(device) {
      return device.type.match(/chime/);
    },
    isGouda(device) {
      return !!device.type.match(/sidewalk_bridge/);
    },
    supportsBitrateInfo(device) {
      return (
        !this.isChime(device) &&
        !this.isJaws(device) &&
        !this.isBlueJay(device) &&
        !this.isGouda(device)
      );
    },
    imageUrl(image_segment) {
      return `${this.baseUrl}/UI/app/assets/${image_segment}`;
    },
    product(device) {
      return this.products.find((p) => p.kind === device.type) || {};
    },
    currentDoorbotIds() {
      return {
        doorbot_ids: this.pagination.pages[this.pagination.currentPage - 1],
        params: {
          customer_id: this.customerId,
        },
      };
    },
    resetPagination() {
      this.pagination.currentPage = 1;
      this.pagination.pages = [];
      this.pagination.total = 0;
      this.pagination.show = false;
    },
    handlePageChange(page) {
      this.getDeviceList();
    },
    getDeviceRegistries() {
      this.loading = true;
      this.resetPagination();
      this.getCustomerLocations();
      this.$nextTick(() => {
        this.fetchDeviceRegistries({
          customer_id: this.customerId,
          params: { location_id: this.selectedLocation.value },
        })
          .then((response) => {
            const ids = get(response, "data.doorbot_ids", []);
            this.pagination.total = ids.length;
            this.pagination.pages = [];
            let a = 0;
            let size = this.pagination.size;
            for (let b = size; a <= this.pagination.total; b += size) {
              this.pagination.pages.push(ids.slice(a, b));
              a = b;
            }
            this.getDeviceList();
          })
          .catch((_error) => {
            console.error(_error);
            this.loading = false;
          });
      });
    },
    getDeviceList() {
      this.loading = true;
      this.errorMessage = false;
      this.fetchDeviceList(this.currentDoorbotIds())
        .then((response) => {
          const message = response.data.message;
          if (message) {
            this.errorMessage = message;
          } else {
            this.errorMessage = false;
            this.pagination.show = this.pagination.total > this.pagination.size;
          }
        })
        .catch((error) => {
          this.errorMessage = "An error occurred. Please try again.";
          console.error(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deviceIds() {
      return this.devices
        .filter((d) => Boolean(d.doorbot_id))
        .map((device) => ({
          device_id: device.mac_address || device.hardware_id,
          doorbot_id: device.doorbot_id,
          owner_id: device.owner_id,
          type: device.type,
        }));
    },
    fetchTroubleshooting() {
      let devices = this.deviceIds();
      if (devices.length === 0) return;
      this.troubleshooting.loading = true;
      this.getDeviceTroubleshooting({ devices })
        .then((response) => {
          this.troubleshooting.devices = response;
        })
        .catch((error) => {
          console.info(error);
        })
        .finally(() => {
          this.troubleshooting.loading = false;
        });
    },
    fetchWarranties() {
      let devices = this.deviceIds();
      if (devices.length === 0) return;
      this.warranty.loading = true;
      this.eligibility = {};
      this.getWarrantyAndEligibilityStatus({
        category: "device",
        devices,
        customerId: this.customerId,
        locationId: this.selectedLocation.value,
      })
        .then((devices) => {
          this.warranty.devices = devices;

          this.devices.forEach((device) => {
            const id = device.mac_address || device.hardware_id;
            const unavailableWarranty = new Warranty().toJson();

            if (!this.warranty.devices.hasOwnProperty(id)) {
              this.warranty.devices[id] = {};
              this.warranty.devices[id].warranty = unavailableWarranty;
            }

            if (device.deleted_at || device.deactivated_at) {
              this.warranty.devices[id].warranty = unavailableWarranty;
            }

            const coverage = devices[device.device_id];
            this.eligibility[device.device_id] =
              DeviceWarrantyAndEligibilityPolicy.create(coverage);
          });
        })
        .catch((error) => {
          console.info(error);
        })
        .finally(() => {
          this.warranty.loading = false;
        });
    },
    fetchStatuses() {
      const payload = this.devices
        .map((device) => ({
          kind: device.type,
          doorbot_id: device.doorbot_id,
        }))
        .filter((item) => Boolean(item.doorbot_id));
      if (payload.length === 0) return;
      this.statuses.loading = true;
      this.getDeviceStatuses({ devices: payload })
        .then((response) => {
          this.statuses.devices = response;
        })
        .catch((error) => {
          console.info(error);
        })
        .finally(() => {
          this.statuses.loading = false;
        });
    },
    openIperfModal(device) {
      this.currentDevice = device;

      console.log("Current device", device);

      forEach(this.iperfOptions, (option) => {
        option.checked = false;
      });

      this.enableIperfTest = true;
    },
    executeTest(device, test_type, done) {
      this.execThroughputTest({
        device_id: device.doorbot_id,
        device_kind: device.type,
        test_type,
      })
        .then(() => {
          this.$message.success("Test executed");
          this.getThroughputTestResults(device.doorbot_id);
        })
        .catch(() => this.$message.error("Could not execute Test"))
        .finally(() => done());
    },
    getThroughputTestResults(doorbotId) {
      let tTest = this.tTest.devices[doorbotId];
      tTest.working = true;
      this.loadLastStatusReport({ doorbotId })
        .then((response) => {
          let data = get(response, "data.context", {});
          if (data.context === IPERF_TEST_CONTEXT) {
            let iperfSettings = keys(response.data.current_settings).filter(
              (name) => name.match(/^iperf_/)
            );
            tTest.result = iperfSettings.map((key) => ({ key: data[key] }));
          }
          console.info("Result", tTest.result);
        })
        .catch((err) => console.error(err))
        .finally(() => (tTest.working = false));
    },
    getFulfillmentStatus(isDelayed) {
      return isDelayed ? "Delayed" : "Standard";
    },
    isResidentialLocation(item) {
      const isResidentialType =
        get(item, "location_type", "residential") === "residential";
      const isLocationOwner = item.smb_role === "owner";
      return isResidentialType && isLocationOwner;
    },
  },
};
</script>

<style lang="scss">
$color-opacity: opacity(0.9);

.el-alert.coverage-alerts {
  cursor: default;
  padding: 5px 6px !important;
  background-color: transparent !important;
  .el-alert__title {
    font-size: 12px !important;
    color: #999999;
    letter-spacing: 0.4px;
    font-weight: 400;
  }
}

.warranty-status {
  margin-top: 5px;
  cursor: help;
  font-size: 13px;
  align-items: center;
  .divider {
    height: auto !important;
  }
}

.block {
  display: block;
}

.el-table__body tr.hover-row > td,
.el-table__body tr.hover-row.current-row > td {
  background-color: inherit;
}

.el-table__body tr.hover-row.el-table__row--striped > td,
.el-table__body tr.hover-row.el-table__row--striped.current-row > td {
  background-color: #fafafa;
}

.device-table-cell-style {
  .cell {
    padding: 0px 10px;
  }
}
.device-table-row-style {
  .cell {
    word-break: break-word;
    padding: 0;
    padding-top: 7px;
    padding-left: 7px;
    min-height: 15vh;
    max-height: 40vh;
  }
}
.device-list .disabled {
  color: #d6d6d6;
}
.device-list .danger {
  color: #f56c6c;
}
.device-list .replace_process {
  color: #e6a23c;
}
.device-list .active {
  color: #67c23a;
}
.device-list .returned {
  color: #f56c6c;
}

.device-list {
  .offline-device__icon {
    color: $--color-milano-red;
    filter: $color-opacity;
  }
  .online-device__icon {
    color: $--color-fun-green;
    filter: $color-opacity;
  }
}

table {
  .column-x1 {
    width: 5% !important;
  }
  .column-x2 {
    width: 10% !important;
  }
  .column-x3 {
    width: 15% !important;
  }
  .column-x4 {
    width: 25% !important;
  }
}

th.el-table_4_column_23,
td.el-table_4_column_23,
th.el-table_4_column_24,
td.el-table_4_column_24 {
  .cell {
    text-align: center;
    .items-start {
      align-items: center !important;
    }
  }
}

.el-input__prefix {
  top: 9px !important;
}

.ring-solution-unavailable {
  font-size: smaller;
}

.custom-divider {
  height: 40px;
  width: 1px;
  background-color: #dcdfe6;
  margin: 0 8px;
}
</style>
